import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  TreePine, 
  Database, 
  AlertTriangle,
  TrendingUp,
  Layers,
  Split,
  FileX,
  BarChart3
} from "lucide-react";

interface BTreeNode {
  id: string;
  level: number;
  keys: number[];
  children?: BTreeNode[];
  pageId: number;
  fillFactor: number;
  isSplit: boolean;
  splitCause?: string;
}

interface IndexStats {
  name: string;
  type: 'CLUSTERED' | 'NONCLUSTERED';
  depth: number;
  pageCount: number;
  avgPageDensity: number;
  fragmentationPercent: number;
  pageSplits: number;
  keyLookups: number;
  scanCount: number;
  seekCount: number;
}

export function BTreeVisualizer() {
  const [selectedIndex, setSelectedIndex] = useState<string>("PK_Users");
  
  const sampleBTree: BTreeNode = {
    id: "root",
    level: 2,
    keys: [500, 1000],
    pageId: 1,
    fillFactor: 85,
    isSplit: false,
    children: [
      {
        id: "leaf1",
        level: 1,
        keys: [100, 200, 300, 400],
        pageId: 2,
        fillFactor: 95,
        isSplit: true,
        splitCause: "High insert volume on clustered index",
        children: [
          { id: "data1", level: 0, keys: [1, 25, 50, 75, 100], pageId: 10, fillFactor: 100, isSplit: false },
          { id: "data2", level: 0, keys: [125, 150, 175, 200], pageId: 11, fillFactor: 80, isSplit: false },
          { id: "data3", level: 0, keys: [225, 250, 275, 300], pageId: 12, fillFactor: 80, isSplit: false },
          { id: "data4", level: 0, keys: [325, 350, 375, 400], pageId: 13, fillFactor: 80, isSplit: false }
        ]
      },
      {
        id: "leaf2",
        level: 1,
        keys: [600, 700, 800, 900],
        pageId: 3,
        fillFactor: 70,
        isSplit: false,
        children: [
          { id: "data5", level: 0, keys: [525, 550, 575, 600], pageId: 14, fillFactor: 80, isSplit: false },
          { id: "data6", level: 0, keys: [625, 650, 675, 700], pageId: 15, fillFactor: 80, isSplit: false },
          { id: "data7", level: 0, keys: [725, 750, 775, 800], pageId: 16, fillFactor: 80, isSplit: false },
          { id: "data8", level: 0, keys: [825, 850, 875, 900], pageId: 17, fillFactor: 80, isSplit: false }
        ]
      },
      {
        id: "leaf3",
        level: 1,
        keys: [1100, 1200, 1300],
        pageId: 4,
        fillFactor: 60,
        isSplit: false,
        children: [
          { id: "data9", level: 0, keys: [1025, 1050, 1075, 1100], pageId: 18, fillFactor: 80, isSplit: false },
          { id: "data10", level: 0, keys: [1125, 1150, 1175, 1200], pageId: 19, fillFactor: 80, isSplit: false },
          { id: "data11", level: 0, keys: [1225, 1250, 1275, 1300], pageId: 20, fillFactor: 80, isSplit: false }
        ]
      }
    ]
  };

  const indexStats: IndexStats[] = [
    {
      name: "PK_Users",
      type: "CLUSTERED",
      depth: 3,
      pageCount: 1247,
      avgPageDensity: 82.5,
      fragmentationPercent: 15.2,
      pageSplits: 234,
      keyLookups: 0,
      scanCount: 45,
      seekCount: 15678
    },
    {
      name: "IX_Users_Email",
      type: "NONCLUSTERED",
      depth: 2,
      pageCount: 156,
      avgPageDensity: 90.1,
      fragmentationPercent: 3.8,
      pageSplits: 12,
      keyLookups: 2341,
      scanCount: 23,
      seekCount: 8901
    },
    {
      name: "IX_Users_CreatedDate",
      type: "NONCLUSTERED",
      depth: 3,
      pageCount: 189,
      avgPageDensity: 75.6,
      fragmentationPercent: 28.4,
      pageSplits: 189,
      keyLookups: 5678,
      scanCount: 67,
      seekCount: 4523
    }
  ];

  const renderBTreeNode = (node: BTreeNode, x: number, y: number, level: number) => {
    const nodeWidth = 120;
    const nodeHeight = 40;
    const levelSpacing = 80;
    const nodeSpacing = 140;

    return (
      <g key={node.id}>
        {/* Node Rectangle */}
        <rect
          x={x - nodeWidth/2}
          y={y - nodeHeight/2}
          width={nodeWidth}
          height={nodeHeight}
          fill={node.isSplit ? "#fef2f2" : node.level === 0 ? "#f0f9ff" : "#f8fafc"}
          stroke={node.isSplit ? "#dc2626" : node.level === 0 ? "#0369a1" : "#64748b"}
          strokeWidth={node.isSplit ? 2 : 1}
          rx={4}
        />
        
        {/* Page ID */}
        <text x={x - nodeWidth/2 + 4} y={y - nodeHeight/2 + 12} fontSize="8" fill="#64748b">
          Page {node.pageId}
        </text>
        
        {/* Keys */}
        <text x={x} y={y} textAnchor="middle" fontSize="10" fill="#1e293b" fontWeight="500">
          [{node.keys.join(", ")}]
        </text>
        
        {/* Fill Factor */}
        <text x={x} y={y + 12} textAnchor="middle" fontSize="8" fill="#64748b">
          {node.fillFactor}% full
        </text>
        
        {/* Split Indicator */}
        {node.isSplit && (
          <circle cx={x + nodeWidth/2 - 8} cy={y - nodeHeight/2 + 8} r="4" fill="#dc2626">
            <title>{node.splitCause}</title>
          </circle>
        )}
        
        {/* Children */}
        {node.children && node.children.map((child, index) => {
          const childX = x + (index - (node.children!.length - 1) / 2) * nodeSpacing;
          const childY = y + levelSpacing;
          
          return (
            <g key={child.id}>
              {/* Connection Line */}
              <line
                x1={x}
                y1={y + nodeHeight/2}
                x2={childX}
                y2={childY - nodeHeight/2}
                stroke="#cbd5e1"
                strokeWidth={1}
              />
              {renderBTreeNode(child, childX, childY, level + 1)}
            </g>
          );
        })}
      </g>
    );
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TreePine className="w-5 h-5" />
            B-Tree Index Analysis & Visualization
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="visualization" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="visualization">B-Tree Structure</TabsTrigger>
              <TabsTrigger value="statistics">Index Statistics</TabsTrigger>
              <TabsTrigger value="fragmentation">Fragmentation Analysis</TabsTrigger>
              <TabsTrigger value="recommendations">Maintenance Plans</TabsTrigger>
            </TabsList>

            <TabsContent value="visualization" className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Index: {selectedIndex}</h3>
                  <p className="text-sm text-gray-600">Clustered Index - Depth: 3 levels</p>
                </div>
                <div className="flex gap-2">
                  {indexStats.map(idx => (
                    <Button
                      key={idx.name}
                      variant={selectedIndex === idx.name ? "default" : "outline"}
                      size="sm"
                      onClick={() => setSelectedIndex(idx.name)}
                    >
                      {idx.name}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="border rounded-lg p-4 bg-gray-50 overflow-x-auto">
                <svg width="800" height="300" viewBox="0 0 800 300">
                  {renderBTreeNode(sampleBTree, 400, 50, 0)}
                </svg>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Layers className="w-4 h-4 text-shiftleft-600" />
                    <span className="font-medium">Page Structure</span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Root Level:</span>
                      <span>1 page</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Intermediate:</span>
                      <span>3 pages</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Leaf Level:</span>
                      <span>11 pages</span>
                    </div>
                    <div className="flex justify-between font-semibold border-t pt-1">
                      <span>Total:</span>
                      <span>15 pages</span>
                    </div>
                  </div>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <Split className="w-4 h-4 text-red-600" />
                    <span className="font-medium">Page Splits</span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Total Splits:</span>
                      <span className="text-red-600 font-semibold">234</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Recent (24h):</span>
                      <span>12</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Cause:</span>
                      <span>High inserts</span>
                    </div>
                    <div className="text-xs text-red-600 mt-2">
                      High split activity detected
                    </div>
                  </div>
                </Card>

                <Card className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <BarChart3 className="w-4 h-4 text-green-600" />
                    <span className="font-medium">Performance</span>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Seeks:</span>
                      <span className="text-green-600 font-semibold">15,678</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Scans:</span>
                      <span>45</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Key Lookups:</span>
                      <span>0</span>
                    </div>
                    <div className="text-xs text-green-600 mt-2">
                      Excellent seek ratio
                    </div>
                  </div>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="statistics" className="space-y-4">
              <div className="space-y-4">
                {indexStats.map((index) => (
                  <Card key={index.name} className="p-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <Database className="w-5 h-5" />
                        <div>
                          <h4 className="font-semibold">{index.name}</h4>
                          <Badge variant={index.type === 'CLUSTERED' ? 'default' : 'secondary'}>
                            {index.type}
                          </Badge>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-gray-600">Health Score</div>
                        <div className={`text-lg font-bold ${
                          index.fragmentationPercent < 10 ? 'text-green-600' :
                          index.fragmentationPercent < 30 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {(100 - index.fragmentationPercent).toFixed(0)}%
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <div className="text-sm text-gray-600">Depth</div>
                        <div className="font-semibold">{index.depth} levels</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Page Count</div>
                        <div className="font-semibold">{index.pageCount.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Avg Density</div>
                        <div className="font-semibold">{index.avgPageDensity}%</div>
                      </div>
                      <div>
                        <div className="text-sm text-gray-600">Fragmentation</div>
                        <div className={`font-semibold ${
                          index.fragmentationPercent < 10 ? 'text-green-600' :
                          index.fragmentationPercent < 30 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {index.fragmentationPercent}%
                        </div>
                      </div>
                    </div>

                    <div className="mt-4 pt-4 border-t">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-gray-600">Page Splits:</span>
                          <span className="ml-2 font-semibold">{index.pageSplits}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Key Lookups:</span>
                          <span className="ml-2 font-semibold">{index.keyLookups.toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Seeks:</span>
                          <span className="ml-2 font-semibold text-green-600">{index.seekCount.toLocaleString()}</span>
                        </div>
                        <div>
                          <span className="text-gray-600">Scans:</span>
                          <span className="ml-2 font-semibold">{index.scanCount}</span>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="fragmentation" className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card className="p-4">
                  <h4 className="font-semibold mb-4 flex items-center gap-2">
                    <FileX className="w-4 h-4 text-red-600" />
                    Fragmentation Hotspots
                  </h4>
                  <div className="space-y-3">
                    <div className="p-3 bg-red-50 border border-red-200 rounded">
                      <div className="font-medium text-red-800">IX_Users_CreatedDate</div>
                      <div className="text-sm text-red-600">28.4% fragmented - Immediate attention required</div>
                      <div className="text-xs mt-1">Caused by sequential inserts with random deletes</div>
                    </div>
                    <div className="p-3 bg-yellow-50 border border-yellow-200 rounded">
                      <div className="font-medium text-yellow-800">PK_Users</div>
                      <div className="text-sm text-yellow-600">15.2% fragmented - Monitor closely</div>
                      <div className="text-xs mt-1">Growing due to page splits from bulk operations</div>
                    </div>
                    <div className="p-3 bg-green-50 border border-green-200 rounded">
                      <div className="font-medium text-green-800">IX_Users_Email</div>
                      <div className="text-sm text-green-600">3.8% fragmented - Healthy</div>
                      <div className="text-xs mt-1">Well-maintained with regular reorganization</div>
                    </div>
                  </div>
                </Card>

                <Card className="p-4">
                  <h4 className="font-semibold mb-4 flex items-center gap-2">
                    <TrendingUp className="w-4 h-4 text-shiftleft-600" />
                    Fragmentation Trends (7 days)
                  </h4>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>PK_Users</span>
                        <span>15.2% (+2.1%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '15.2%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>IX_Users_CreatedDate</span>
                        <span>28.4% (+5.7%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-red-500 h-2 rounded-full" style={{ width: '28.4%' }}></div>
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span>IX_Users_Email</span>
                        <span>3.8% (-0.2%)</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-green-500 h-2 rounded-full" style={{ width: '3.8%' }}></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-shiftleft-50 border border-shiftleft-200 rounded">
                    <div className="text-sm font-medium text-shiftleft-800">Recommendation</div>
                    <div className="text-xs text-shiftleft-600 mt-1">
                      Schedule index rebuild for IX_Users_CreatedDate during next maintenance window.
                      Consider partitioning strategy for large tables with time-based patterns.
                    </div>
                  </div>
                </Card>
              </div>
            </TabsContent>

            <TabsContent value="recommendations" className="space-y-4">
              <div className="space-y-4">
                <Card className="p-4">
                  <h4 className="font-semibold mb-4">Automated Maintenance Recommendations</h4>
                  
                  <div className="space-y-4">
                    <div className="p-4 bg-red-50 border border-red-200 rounded">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                        <div className="flex-1">
                          <div className="font-medium text-red-800">Critical: Index Rebuild Required</div>
                          <div className="text-sm text-red-600 mt-1">
                            IX_Users_CreatedDate has 28.4% fragmentation
                          </div>
                          <div className="mt-2 p-2 bg-white rounded border text-xs font-mono">
                            ALTER INDEX IX_Users_CreatedDate ON Users REBUILD{'\n'}
                            WITH (FILLFACTOR = 85, ONLINE = ON);
                          </div>
                          <div className="text-xs text-red-600 mt-2">
                            Estimated time: 15 minutes | Estimated benefit: 40% query performance improvement
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded">
                      <div className="flex items-start gap-3">
                        <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                        <div className="flex-1">
                          <div className="font-medium text-yellow-800">Moderate: Index Reorganization</div>
                          <div className="text-sm text-yellow-600 mt-1">
                            PK_Users shows moderate fragmentation at 15.2%
                          </div>
                          <div className="mt-2 p-2 bg-white rounded border text-xs font-mono">
                            ALTER INDEX PK_Users ON Users REORGANIZE;{'\n'}
                            UPDATE STATISTICS Users;
                          </div>
                          <div className="text-xs text-yellow-600 mt-2">
                            Estimated time: 5 minutes | Can run online during business hours
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-shiftleft-50 border border-shiftleft-200 rounded">
                      <div className="flex items-start gap-3">
                        <Database className="w-5 h-5 text-shiftleft-600 mt-0.5" />
                        <div className="flex-1">
                          <div className="font-medium text-shiftleft-800">Optimization: Fill Factor Adjustment</div>
                          <div className="text-sm text-shiftleft-600 mt-1">
                            Reduce page splits by adjusting fill factors for high-insert tables
                          </div>
                          <div className="mt-2 p-2 bg-white rounded border text-xs font-mono">
                            {`-- For tables with sequential inserts
ALTER INDEX PK_Users ON Users REBUILD WITH (FILLFACTOR = 80);
ALTER INDEX IX_Users_CreatedDate ON Users REBUILD WITH (FILLFACTOR = 75);`}
                          </div>
                          <div className="text-xs text-shiftleft-600 mt-2">
                            Prevents future page splits | Reduces maintenance overhead
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-4">
                  <h4 className="font-semibold mb-4">Maintenance Schedule Template</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <h5 className="font-medium mb-2">Daily (Off-hours)</h5>
                      <ul className="text-sm space-y-1">
                        <li>• Update statistics on high-activity tables</li>
                        <li>• Check for blocking processes</li>
                        <li>• Monitor fragmentation levels</li>
                        <li>• Review query execution plans</li>
                      </ul>
                    </div>
                    <div>
                      <h5 className="font-medium mb-2">Weekly (Maintenance Window)</h5>
                      <ul className="text-sm space-y-1">
                        <li>• Reorganize indexes 10-30% fragmented</li>
                        <li>• Rebuild indexes &gt;30% fragmented</li>
                        <li>• Update statistics on all tables</li>
                        <li>• Cleanup query plan cache</li>
                      </ul>
                    </div>
                  </div>
                </Card>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}